import type React from "react";
import { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  Typography,
  Grid,
  Box,
  CardMedia,
  CircularProgress,
} from "@mui/material";
import { CheckCircle } from "@mui/icons-material";
import { useTheme } from "@mui/material/styles";
import type { CoverPageTemplate } from "../types/cover.types";
import { COVER_TEMPLATES } from "../proposal-preview/templates/coverTemplates";

interface CoverPageSelectorProps {
  selectedTemplate: CoverPageTemplate | null;
  onTemplateSelect: (template: CoverPageTemplate | null) => void;
  bookingType: "FUNERAL" | "EVENT" | "CONFERENCE";
  selectedTemplateId?: number | null;
}

const CoverPageSelector: React.FC<CoverPageSelectorProps> = ({
  selectedTemplate,
  onTemplateSelect,
  bookingType,
  selectedTemplateId,
}) => {
  const theme = useTheme();
  const [templates, setTemplates] = useState<CoverPageTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  //const [error, setError] = useState<string | null>(null);

  // useEffect(() => {
  //   const fetchTemplates = async () => {
  //     try {
  //       setLoading(true);
  //       const response = await apiService.get<CoverPageTemplate[]>(
  //         "/api/configurations/proposal-templates/",
  //       );

  //       const data = response.data;
  //       const categoryMap: Record<string, string> = {
  //         FUNERAL: "funeral",
  //         EVENT: "event",
  //         CONFERENCE: "conference",
  //       };

  //       const filteredTemplates = data.filter(
  //         (template) =>
  //           template.is_active &&
  //           template.category.toLowerCase() === categoryMap[bookingType],
  //       );

  //       setTemplates(filteredTemplates);

  //       // If there is a saved template ID, use it
  //       if (!selectedTemplate && selectedTemplateId) {
  //         const found = filteredTemplates.find(
  //           (t) => t.id === selectedTemplateId,
  //         );
  //         if (found) {
  //           onTemplateSelect(found);
  //           return;
  //         }
  //       }

  //       // If no saved template AND nothing selected → select first template
  //       if (!selectedTemplate && filteredTemplates.length > 0) {
  //         onTemplateSelect(filteredTemplates[0]);
  //       }

  //       // If selected template is not in the filtered list → reset to first
  //       if (
  //         selectedTemplate &&
  //         !filteredTemplates.some((t) => t.id === selectedTemplate.id)
  //       ) {
  //         onTemplateSelect(filteredTemplates[0] ?? null);
  //       }

  //       setError(null);
  //     } catch (err) {
  //       console.error("Error fetching templates:", err);
  //       setError("Failed to load templates. Please try again.");
  //     } finally {
  //       setLoading(false);
  //     }
  //   };

  //   fetchTemplates();
  // }, [bookingType, onTemplateSelect, selectedTemplate, selectedTemplateId]);

  useEffect(() => {
    const categoryMap: Record<string, string> = {
      FUNERAL: "funeral",
      EVENT: "event",
      CONFERENCE: "conference",
    };

    const filteredTemplates = COVER_TEMPLATES.filter(
      (template) =>
        template.is_active &&
        template.category.toLowerCase() === categoryMap[bookingType],
    );

    setTemplates(filteredTemplates);
    setLoading(false);

    if (!selectedTemplate && selectedTemplateId) {
      const found = filteredTemplates.find((t) => t.id === selectedTemplateId);
      if (found) {
        onTemplateSelect(found);
        return;
      }
    }

    if (!selectedTemplate && filteredTemplates.length > 0) {
      onTemplateSelect(filteredTemplates[0]);
    }

    if (
      selectedTemplate &&
      !filteredTemplates.some((t) => t.id === selectedTemplate.id)
    ) {
      onTemplateSelect(filteredTemplates[0] ?? null);
    }
  }, [bookingType, onTemplateSelect, selectedTemplate, selectedTemplateId]);

  if (loading) {
    return (
      <Card sx={{ mt: 3, boxShadow: 0 }}>
        <CardContent sx={{ p: 4, display: "flex", justifyContent: "center" }}>
          <CircularProgress />
        </CardContent>
      </Card>
    );
  }

  // if (error) {
  //   return (
  //     <Card sx={{ mt: 3, boxShadow: 0 }}>
  //       <CardContent sx={{ p: 4 }}>
  //         <Typography color="error">{error}</Typography>
  //       </CardContent>
  //     </Card>
  //   );
  // }

  if (templates.length === 0) {
    return (
      <Card sx={{ mt: 3, boxShadow: 0 }}>
        <CardContent sx={{ p: 4 }}>
          <Typography color="text.secondary">
            No templates available for {bookingType.toLowerCase()} proposals.
          </Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card sx={{ mt: 3, boxShadow: 0 }}>
      <CardContent sx={{ p: 4 }}>
        <Typography variant="h5" gutterBottom sx={{ mb: 3 }}>
          Select Cover Page Template
        </Typography>
        <Grid container spacing={3}>
          {templates.map((template) => (
            <Grid size={{ xs: 12, sm: 6, md: 6 }} key={template.id}>
              <Card
                sx={{
                  position: "relative",
                  height: "100%",
                  boxShadow: 0,
                  border:
                    selectedTemplate?.id === template.id
                      ? "2px solid"
                      : "1px solid",
                  borderColor:
                    selectedTemplate?.id === template.id
                      ? theme.palette.primary.main
                      : "divider",
                  cursor: "pointer",
                  transition: "all 0.3s ease",
                  "&:hover": {
                    boxShadow: 4,
                    borderColor: theme.palette.primary.main,
                  },
                }}
                onClick={() => onTemplateSelect(template)}
              >
                {selectedTemplate?.id === template.id && (
                  <Box
                    sx={{
                      position: "absolute",
                      top: 8,
                      right: 8,
                      zIndex: 1,
                      bgcolor: "background.paper",
                      borderRadius: "50%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <CheckCircle
                      sx={{
                        fontSize: 32,
                        color: theme.palette.primary.main,
                      }}
                    />
                  </Box>
                )}

                <CardMedia
                  component="img"
                  height="200"
                  image={template.background_image}
                  alt={template.name}
                  sx={{ objectFit: "cover" }}
                />

                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    {template.name}
                  </Typography>
                  {template.description && (
                    <Typography variant="body2" color="text.secondary">
                      {template.description}
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </CardContent>
    </Card>
  );
};

export default CoverPageSelector;
